import { AfterViewInit, Component, EventEmitter, Inject, OnInit, Output } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { DatapointsDataService, DataPoint } from '@services/datapoints-data.service';
import { combineLatest, distinctUntilChanged, filter, finalize, Subject, takeUntil } from 'rxjs';
import { GlobalFunctions, SnackType, imagePath, imgTypes } from '@global/GlobalFunctions';
import { UnitsDataService } from '@services/units-data.service';
import { FilesService } from '@services/files.service';
import { siteSubscriptions } from '@services/siteSubscriptions';
import { Account, IAccount } from '@services/accounts.service';
import { ConfirmDialogComponent } from 'src/app/dialogs/confirm-dialog/confirm-dialog.component';

/**
 * Component
 */
@Component({
	selector: 'app-edit-sensor-datapoint',
	templateUrl: './edit-sensor-datapoint.component.html',
	styleUrls: ['./edit-sensor-datapoint.component.scss']
})
export class EditSensorDatapointComponent implements OnInit, AfterViewInit {

	/** Path to Sensor Images */
	public imagePathSensors(): string {
		return imagePath(this.selectedAccountGUID, imgTypes.sensors).toLowerCase()
	}

	/** Path to Datapoint Images */
	public imagePathDatapoints(): string {
		return imagePath(this.selectedAccountGUID, imgTypes.datapoints).toLowerCase()
	}

	/** Update the Image */
	@Output() imageUpdated = new EventEmitter<any>();

	/** Form Group */
	dialogForm: FormGroup;

	/** Enable the New Picture Button */
	public enablePicture: boolean = true;
	/** Enable the Camera Button */
	public enableCamera: boolean = true;
	/** Enable the Delete Button */
	public enableDelete: boolean = false;

	/** Update Image Instantly */
	public numberRND: number = Math.random();
	/** new Image Selected */
	public newImage: boolean;

	/** File */
	public file: any
	/** Filename */
	public fileName: string
	/** Loading */
	public loading: boolean
	/** shortLink */
	public shortLink: any
	/** SI Units */
	public siUnits: any;
	/** Fields */
	public fields: Object = { text: 'SI', id: 'UnitsID' };
	/** Value */
	public value: string = '';
	/** WaterMark */
	public waterMark: string = 'Select Units';

	/** Observable Subject Finalise */
	private finalise = new Subject<void>();

	/** selectedAccount */
	private selectedAccount: IAccount;

	/** selectedAccountGUID */
	private selectedAccountGUID: string;

	/**
	 * Default Constructor
	 * @param fb Form Builder
	 * @param dialogRef Referense to the Dialog box
	 * @param unitsDataService Units Data Service
	 * @param globalFunctions Global Functions
	 * @param filesService File Services
	 * @param siteSubscriptions  SiteSubscriptions
	 * @param dialogData  object interface
	 */
	constructor(
		private fb: FormBuilder,
		public dialogRef: MatDialogRef<EditSensorDatapointComponent>,
		private unitsDataService: UnitsDataService,
		private globalFunctions: GlobalFunctions,
		private filesService: FilesService,
		private siteSubscriptions: siteSubscriptions,
		@Inject(MAT_DIALOG_DATA) public dialogData: DataPoint,
	) {
		combineLatest([
			this.siteSubscriptions.selectedAccount$.pipe(takeUntil(this.finalise), distinctUntilChanged())
		]).pipe(
			// Ensure that subscriptions are not null or undefined
			filter(([account]) => !!account)
		).subscribe({
			next: ([account]) => {
				this.selectedAccount = account;
				this.selectedAccount = this.siteSubscriptions.normalizeFeatures(this.selectedAccount) as Account;
				this.selectedAccountGUID = this.selectedAccount.GUID;
			},
			error: (error) => { this.globalFunctions.HandleError(error, "Error: Error in one of the combined subscriptions", false); }
		});
	}

	/**
	 * Page after view init Event Handler
	 */
	ngAfterViewInit(): void { }

	/**
	 * Page On Initialise Event Handler
	 */
	ngOnInit(): void {
		this.dialogForm = this.fb.group({
			SensorName: [this.dialogData.SensorName || '', Validators.required],
			DataPointID: [this.dialogData.DataPointID || 0],
			EngMax: [this.dialogData.EngMax || 32767, [Validators.required, Validators.min(0)]],
			EngMin: [this.dialogData.EngMin || 0, [Validators.required, Validators.min(0)]],
			ImageURL: [this.dialogData.ImageURL || ''],
			Offline: [this.dialogData.Offline || false],
			PV: [this.dialogData.PV || 0, Validators.required],
			PVName: [this.dialogData.PVName || '', Validators.required],
			PVDescription: [this.dialogData.PVDescription || ''],
			SI: [this.dialogData.SI || ''],
			SIIndex: [this.dialogData.SIIndex || 0],
			Enabled: [this.dialogData.Enabled || false],
			Modified: [Math.trunc(Math.random() * 100)],
			EnableOffline: [this.dialogData.EnableOffline || false],
			ImageSrc: this.imagePathDatapoints() + this.dialogData.DataPointID + '?' + this.numberRND,
			IsCumulative: [this.dialogData.IsCumulative || false],

			IsBinary: [this.dialogData.IsBinary || false],
			BinaryOnValue: [this.dialogData.BinaryOnValue || 'On'],
			BinaryOffValue: [this.dialogData.BinaryOffValue || 'Off'],
			BinaryThresholdValue: [this.dialogData.BinaryThresholdValue || '0.5']

		});

		this.GetSIUnits();
	}

	/**
	 * Cancel button for dialog
	 */
	onCancelClick(): void {
		if (this.dialogForm.dirty) {
			this.globalFunctions.UserConfirm$('Lose any unsaved changes?').subscribe(OKResult => {
				if (OKResult) { this.dialogRef.close(); }
				// do nothing if result is false
			});
		} else {
			this.dialogRef.close();
		}
	}

	/**
	 * Save the form data and image if needed
	 */
	async onSaveClick(): Promise<void> {

		if (this.newImage && this.file) {
			try {
				const accountGUID = this.selectedAccountGUID.toLowerCase();
				const fileName = this.dialogData.DataPointID.toString();
				const location = "datapoints";
				await this.filesService.saveImage(accountGUID, fileName, this.file, location); // wait for upload to complete
				this.newImage = false;
				this.file = undefined;
			} // Optional: handle upload failure (maybe prevent closing the dialog)
			catch (error) { return; }
		}

		// Now the upload is done, close the dialog with the form value
		this.dialogRef.close(this.dialogForm.value);
	}

	/**
	 * Delete Image
	 */
	deleteImg(): void {
		// Extract and store the file name
		this.fileName = "transparent.png";
		this.file = new File([""], "transparent.png", { type: "image/png" });
		this.dialogForm.markAsDirty();
		this.newImage = true;
	}

	/**
	 * New File deteced
	 * @param file File from child component
	 */
	onNewImage(file: File | null): void {
		/** Check for a valid file */
		this.file = file ? file : undefined;
		this.dialogForm.markAsDirty();
		this.newImage = true;
	}

	GetSIUnits() {
		// Create a local Subject to control the subscription lifecycle
		const fetchDataSubject = new Subject<void>();

		this.unitsDataService.getSIUnits$().pipe(
			takeUntil(fetchDataSubject), distinctUntilChanged(),
			filter((sIUnits) => !!sIUnits),
			finalize(() => {
				// Ensure the subscription is terminated and the subject is completed
				fetchDataSubject.next();
				fetchDataSubject.complete();
			})
		).subscribe({
			next: (sIUnits: any) => {
				try {
					this.siUnits = sIUnits;
				} catch (error) {
					this.globalFunctions.HandleError(error, "Error: this.unitsDataService.getSIUnits.next", false);
				}
			},
			error: (error) => {
				if (error.status != 404) {
					this.globalFunctions.HandleError(error, "Error: this.unitsDataService.getSIUnits$", false);
				}
			},
			//complete: () => { console.log("this.unitsDataService.getSIUnits$.subscribe: Complete"); }
		});
	}

	/**
	 * Handles the Units dropdown list change event
	 */
	onUnitsChange(event: any): void {

		let units = this.siUnits.find((X: { SI: any; }) => X.SI.toLowerCase() == event.itemData["SI"].toLowerCase())

		if (units) {
			if (this.dialogForm.get("SIIndex")?.value != units["UnitsID"]) {
				this.dialogForm.get("SIIndex")?.setValue(units["UnitsID"]);
				this.dialogForm.get("SI")?.setValue(units["SI"]);
				this.dialogForm.markAsDirty();
			}
		} else {
			this.dialogForm.get("SIIndex")?.setValue(0);
		}
	}
}
